import { useEffect, useState } from "react";
import axios from "axios";
import useFooddyStore from "../../store";
import { randomAvatar } from "./avatars";

const api = (path, body) =>
  axios.post(`${process.env.REACT_APP_BASEURL}/api/${path}`, body).then((response) => response.data);

const summaryOf = (data) => (data?.success ? data : null);

// Month totals for a year from /yearlyproductdetails (Jan .. current month).
const monthsOf = (data) => (Array.isArray(data) ? data.map((value) => Number(value) || 0) : []);

// Everything the overview shows for the user in the URL, for the selected
// platform. Also fills the shared store fields the share page reads.
const useOverviewData = (userId, platform, year) => {
  const store = useFooddyStore();
  const [data, setData] = useState({ loading: true });
  const [months, setMonths] = useState({ loading: true, current: [], previous: [] });

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    setData({ loading: true });

    Promise.allSettled([
      api(`${platform}/product`, { userId }),
      api("amazon/spendingsummary", { userId }),
      api("flipkart/spendingsummary", { userId }),
      api(`${platform}/shareprofile`, { userId }),
      api(`${platform}/leaderboardapi`, { userId }),
      axios.get(`${process.env.REACT_APP_BASEURL}/api/dashboard/totalusers`).then((r) => r.data),
    ]).then((results) => {
      if (cancelled) return;
      const [product, amazon, flipkart, profile, leaders, users] = results.map((r) =>
        r.status === "fulfilled" ? r.value : null
      );

      const noUser = !product?.totalAmountSpent;
      store.setTotalAmount(noUser ? product?.error : String(product.totalAmountSpent));
      store.setDishesList(noUser ? [] : product.topOrderedProducts);
      store.setNoUser(noUser);
      store.setAmountLoader(false);

      const rankData = profile?.userRankData;
      if (rankData) {
        store.setUserName(rankData.username);
        store.setMonthlyAmt(rankData.total_amount);
        store.setRank(rankData.order_amount_rank);
        store.setGender(rankData.gender);
        store.setUserPrflPic(randomAvatar(rankData.gender));
      }
      store.setProfileLoader(false);

      if (leaders?.combinedLeaderboard) {
        store.setLeaderData(leaders.combinedLeaderboard);
        store.setLeaderId(leaders.userId);
      }
      store.setLeaderboardLoader(false);

      setData({
        loading: false,
        noUser,
        total: Number(product?.totalAmountSpent) || 0,
        topProducts: product?.topOrderedProducts ?? [],
        summaries: { amazon: summaryOf(amazon), flipkart: summaryOf(flipkart) },
        profile: rankData ?? null,
        leaders: leaders?.combinedLeaderboard ?? null,
        leaderId: leaders?.userId ?? null,
        userCount: Number(platform === "amazon" ? users?.amazonUsers : users?.flipkartUsers) || 0,
      });
    });

    return () => {
      cancelled = true;
    };
  }, [userId, platform]);

  useEffect(() => {
    if (!userId || !year) return;
    let cancelled = false;
    setMonths((current) => ({ ...current, loading: true }));
    Promise.allSettled([
      api(`${platform}/yearlyproductdetails`, { userId, year }),
      api(`${platform}/yearlyproductdetails`, { userId, year: year - 1 }),
    ]).then(([current, previous]) => {
      if (cancelled) return;
      setMonths({
        loading: false,
        current: current.status === "fulfilled" ? monthsOf(current.value) : [],
        previous: previous.status === "fulfilled" ? monthsOf(previous.value) : [],
      });
    });
    return () => {
      cancelled = true;
    };
  }, [userId, platform, year]);

  return { ...data, months };
};

export default useOverviewData;
